import type { ReactNode } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
	IconCheckOutline18 as CheckIcon,
	IconEyeSlashOutline18 as EyeOff,
	IconXmarkOutline18 as XmarkIcon,
} from "nucleo-ui-outline-18"
import { fromPairs, groupBy, pipe, sortBy, toPairs } from "remeda"

import type { RankUpdate } from "@productlane/lib/src/roadmap-custom-order"
import type { Issue } from "@productlane/zero"
import {
	getRoadmapIssuesText,
	getRoadmapStatusText,
} from "@productlane/api/src/browser"
import { getProjectStatusSortOrder } from "@productlane/lib/src/project-status-order"
import { orderGroupsByCustom } from "@productlane/lib/src/roadmap-custom-order"
import { Button, Empty } from "@productlane/ui"

import type { ReorderRow } from "./roadmap-reorder-dnd"
import type {
	AugmentedIssue,
	AugmentedProject,
} from "@/lib/project-or-issue-helpers"
import EmptyProject from "@/assets/empty-states/empty-project.png"
import { ProjectOrIssueListWrapper } from "@/components/issue-or-project-items/project-or-issue-list-wrapper"
import { RoleCheck } from "@/components/shared/role-check"
import { PortalColumnsHeader } from "@/components/shared/visibility-toggle"
import { useVisibilityActions } from "@/lib/hooks/useVisibilityActions"
import { groupProjectsByLinearRoadmaps } from "@/lib/project-or-issue-helpers"
import { useRole } from "@/lib/useRole"
import { useZeroWorkspace } from "@/lib/zero"
import { roadmapEmptyState } from "./empty-state"
import { ListBanner } from "./list-banner"
import {
	prunePendingRanks,
	roadmapListId,
	statusGroupKey,
	withPendingRanks,
} from "./reorder"
import { RoadmapReorderDnd } from "./roadmap-reorder-dnd"
import { SortableStatusGroup, StatusGroup } from "./status-group"
import { groupByStatusName } from "./status-groups"

const ISSUE_PAGE_SIZE = 50

const issueStatusSorting: Record<string, number> = {
	triage: 0,
	started: 1,
	unstarted: 2,
	backlog: 3,
}

const getIssueStatusOrder = (
	value: readonly [Issue, ...Issue[]],
): number | undefined => {
	return value.length > 0
		? issueStatusSorting[value[0]?.status ?? ""]
		: undefined
}

const getProjectStatusOrder = (value: readonly AugmentedProject[]): number => {
	if (value.length === 0) return Infinity
	return getProjectStatusSortOrder(value[0]?.state)
}

// Tiebreaker for status groups that share a status type (e.g. custom statuses
// "In Beta" and "In Progress" both map to "started"): the group's lowest Linear
// sortOrder. Keeps the dashboard and the portal in the same order.
const getProjectGroupSortOrder = (value: readonly AugmentedProject[]): number =>
	value.reduce((min, p) => Math.min(min, p.sortOrder), Infinity)

const projectGroupLabel = (project: AugmentedProject) =>
	project.stateName ?? getRoadmapStatusText(project.state ?? "backlog")

const issueGroupLabel = (issue: Issue) =>
	issue.statusName ?? getRoadmapIssuesText(issue.status) ?? "Backlog"

export const TabContent = ({
	filteredProjects: filteredProjectsProp,
	issues: issuesProp,
	maxMapProjects,
	tabValue,
	onTabChange,
	isLoading,
	workspaceId,
	hasMoreRows = false,
	onLoadMoreRows,
	renderTrailingAction,
	pendingCount = 0,
	hasActiveFilters = false,
	onClearFilters,
}: {
	filteredProjects: AugmentedProject[]
	issues: Issue[]
	maxMapProjects: Map<string, number>
	tabValue: "active" | "hidden" | "all" | "pending"
	onTabChange: (value: string) => void
	isLoading: boolean
	workspaceId?: string
	// The list holds one page; scrolling to the end asks the server for more.
	hasMoreRows?: boolean
	onLoadMoreRows?: () => void
	renderTrailingAction?: (
		type: "project" | "issue",
		data: { id: string },
	) => ReactNode
	pendingCount?: number
	hasActiveFilters?: boolean
	onClearFilters?: () => void
}) => {
	const { bulkUpdateVisibility } = useVisibilityActions()

	const [pendingRanks, setPendingRanks] = useState<ReadonlyMap<string, number>>(
		() => new Map(),
	)
	const [pendingGroupOrder, setPendingGroupOrder] = useState<string[] | null>(
		null,
	)
	const filteredProjects = useMemo(
		() => withPendingRanks(filteredProjectsProp, pendingRanks),
		[filteredProjectsProp, pendingRanks],
	)
	const issues = useMemo(
		() => withPendingRanks(issuesProp, pendingRanks),
		[issuesProp, pendingRanks],
	)
	useEffect(() => {
		setPendingRanks((pending) =>
			prunePendingRanks(pending, [...filteredProjectsProp, ...issuesProp]),
		)
	}, [filteredProjectsProp, issuesProp])

	const handleGoToHidden = useCallback(() => {
		onTabChange("hidden")
	}, [onTabChange])

	const sortedByStatusProjects = useMemo(() => {
		const groupedByStatusName = groupBy(filteredProjects, projectGroupLabel)

		return pipe(
			toPairs(groupedByStatusName),
			sortBy(
				([_, value]) => getProjectStatusOrder(value),
				([_, value]) => getProjectGroupSortOrder(value),
			),
			fromPairs,
		) as Record<string, AugmentedProject[]>
	}, [filteredProjects])

	// Every issue keeps its own row, project or not: an issue rolled into its
	// project row is one nobody can find or publish on its own.
	const filteredIssues = issues

	const filteredIssuesCount = filteredIssues.length
	const totalProjectsCount = filteredProjects.length

	const [visibleIssueCount, setVisibleIssueCount] = useState(ISSUE_PAGE_SIZE)

	useEffect(() => {
		setVisibleIssueCount(ISSUE_PAGE_SIZE)
	}, [tabValue])

	const visibleIssues = useMemo(
		() => filteredIssues.slice(0, visibleIssueCount),
		[filteredIssues, visibleIssueCount],
	)
	const hasMoreIssues = filteredIssues.length > visibleIssueCount

	const sortedByStatusIssues = useMemo(() => {
		const groupedByStatusNameIssues = groupBy(visibleIssues, issueGroupLabel)

		return pipe(
			toPairs(groupedByStatusNameIssues),
			sortBy(([_, value]) => {
				if (value.length === 0) {
					return Infinity
				}

				const order = getIssueStatusOrder(value)
				return order !== undefined ? order : Infinity
			}),
			fromPairs,
		) as Record<string, AugmentedIssue[]>
	}, [visibleIssues])

	const workspace = useZeroWorkspace()
	const { role } = useRole()
	const isCustomSort = workspace?.roadmapSettings?.sortOrder === "CUSTOM"
	const savedGroupOrder = workspace?.roadmapSettings?.groupOrder
	const storedGroupOrder = useMemo(
		() => pendingGroupOrder ?? savedGroupOrder ?? [],
		[pendingGroupOrder, savedGroupOrder],
	)
	useEffect(() => {
		const saved = savedGroupOrder ?? []
		if (
			pendingGroupOrder?.length === saved.length &&
			pendingGroupOrder.every((key, index) => key === saved[index])
		)
			setPendingGroupOrder(null)
	}, [pendingGroupOrder, savedGroupOrder])
	const addPendingRanks = useCallback((orders: RankUpdate[]) => {
		setPendingRanks((pending) => {
			const next = new Map(pending)
			for (const order of orders) next.set(order.id, order.portalSortOrder)
			return next
		})
	}, [])
	const clearPending = useCallback(() => {
		setPendingRanks(new Map())
		setPendingGroupOrder(null)
	}, [])
	// The Active tab is what the portal shows, so it is the only list to arrange.
	const canReorder =
		isCustomSort && tabValue === "active" && !!role && role !== "VIEWER"

	const statusGroups = useMemo(() => {
		const groups = groupByStatusName(
			sortedByStatusProjects,
			sortedByStatusIssues,
		)
		return isCustomSort
			? orderGroupsByCustom(groups, statusGroupKey, storedGroupOrder)
			: groups
	}, [
		sortedByStatusProjects,
		sortedByStatusIssues,
		isCustomSort,
		storedGroupOrder,
	])

	const reorderGroups = useMemo(
		() =>
			statusGroups.map((group) => ({
				key: statusGroupKey(group),
				label: group.label,
			})),
		[statusGroups],
	)

	// Ranks come from every loaded row, not just the rendered page of issues.
	const rowsByListId = useMemo(() => {
		const rows = new Map<string, ReorderRow[]>()
		if (!canReorder) return rows
		for (const [label, projects] of Object.entries(sortedByStatusProjects)) {
			const { projectsWithRoadmaps, projectsWithoutRoadmaps } =
				groupProjectsByLinearRoadmaps(
					projects,
					workspace?.isRoadmapsEnabled ?? false,
				)
			rows.set(roadmapListId("project", label), projectsWithoutRoadmaps)
			for (const roadmap of projectsWithRoadmaps) {
				rows.set(roadmapListId("project", label, roadmap.id), roadmap.projects)
			}
		}
		for (const issue of filteredIssues) {
			const listId = roadmapListId("issue", issueGroupLabel(issue))
			const list = rows.get(listId) ?? []
			list.push(issue)
			rows.set(listId, list)
		}
		return rows
	}, [
		canReorder,
		sortedByStatusProjects,
		filteredIssues,
		workspace?.isRoadmapsEnabled,
	])

	const sentinelRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!hasMoreIssues && !hasMoreRows) return
		const sentinel = sentinelRef.current
		if (!sentinel) return
		const observer = new IntersectionObserver(
			(entries) => {
				if (!entries[0]?.isIntersecting) return
				if (hasMoreIssues) {
					setVisibleIssueCount((n) => n + ISSUE_PAGE_SIZE)
					return
				}
				// Rendered everything this page holds: pull the next one.
				onLoadMoreRows?.()
			},
			{ rootMargin: "400px" },
		)
		observer.observe(sentinel)
		return () => observer.disconnect()
	}, [hasMoreIssues, hasMoreRows, onLoadMoreRows, visibleIssueCount])

	const isPendingTab = tabValue === "pending"

	const pendingItemCount = isPendingTab
		? filteredProjects.length + filteredIssues.length
		: 0

	const publishAllPending = useCallback(async () => {
		if (filteredProjects.length > 0) {
			await bulkUpdateVisibility({
				type: "project",
				ids: filteredProjects.map((p) => p.id),
				value: true,
			})
		}
		if (filteredIssues.length > 0) {
			await bulkUpdateVisibility({
				type: "issue",
				ids: filteredIssues.map((i) => i.id),
				value: true,
			})
		}
	}, [bulkUpdateVisibility, filteredProjects, filteredIssues])

	const hideAllPending = useCallback(async () => {
		if (filteredProjects.length > 0) {
			await bulkUpdateVisibility({
				type: "project",
				ids: filteredProjects.map((p) => p.id),
				value: false,
			})
		}
		if (filteredIssues.length > 0) {
			await bulkUpdateVisibility({
				type: "issue",
				ids: filteredIssues.map((i) => i.id),
				value: false,
			})
		}
	}, [bulkUpdateVisibility, filteredProjects, filteredIssues])

	const hasAnyContent = totalProjectsCount > 0 || filteredIssuesCount > 0

	if (!hasAnyContent && !isLoading && workspaceId) {
		const empty = roadmapEmptyState({
			tabValue,
			hasActiveFilters,
			pendingCount,
		})

		return (
			<div className="flex min-h-0 flex-1 items-center justify-center">
				<Empty
					title={empty.title}
					text={empty.text}
					image={
						<img
							src={EmptyProject}
							alt="No requests"
							className="aspect-auto h-[58.5px]"
						/>
					}
					action={
						empty.action === "clear-filters" ? (
							<Button
								variant="outline"
								onClick={onClearFilters}
								Icon={XmarkIcon}
							>
								Clear filters
							</Button>
						) : empty.action === "view-hidden" ? (
							<Button
								variant="outline"
								onClick={handleGoToHidden}
								Icon={EyeOff}
							>
								View hidden
							</Button>
						) : undefined
					}
				/>
			</div>
		)
	}

	return (
		<ProjectOrIssueListWrapper>
			{isPendingTab && pendingItemCount > 0 && (
				<RoleCheck not={["VIEWER"]}>
					<ListBanner
						tone="info"
						label={`${pendingItemCount} pending ${pendingItemCount === 1 ? "request" : "requests"} waiting for review`}
					>
						<Button
							variant="outline"
							Icon={XmarkIcon}
							onClick={() => {
								void hideAllPending()
							}}
						>
							Hide all
						</Button>
						<Button
							variant="outline"
							Icon={CheckIcon}
							onClick={() => {
								void publishAllPending()
							}}
						>
							Publish all
						</Button>
					</ListBanner>
				</RoleCheck>
			)}

			{!isPendingTab && <PortalColumnsHeader />}

			<RoadmapReorderDnd
				enabled={canReorder}
				workspaceId={workspaceId}
				groups={reorderGroups}
				storedGroupOrder={storedGroupOrder}
				rowsByListId={rowsByListId}
				onRowsReordered={addPendingRanks}
				onGroupsReordered={setPendingGroupOrder}
				onReorderFailed={clearPending}
			>
				{({ groupDragActive }) =>
					statusGroups.map((group, index) =>
						canReorder ? (
							<SortableStatusGroup
								key={group.label}
								groupKey={reorderGroups[index]!.key}
								groupDragActive={groupDragActive}
								group={group}
								maxMapProjects={maxMapProjects}
								isPending={isPendingTab}
								renderTrailingAction={renderTrailingAction}
							/>
						) : (
							<StatusGroup
								key={group.label}
								group={group}
								maxMapProjects={maxMapProjects}
								isPending={isPendingTab}
								renderTrailingAction={renderTrailingAction}
							/>
						),
					)
				}
			</RoadmapReorderDnd>

			{hasMoreIssues && <div ref={sentinelRef} aria-hidden className="h-px" />}

			{tabValue === "active" && (
				<div className="px-6 py-4">
					<Button variant="outline" onClick={handleGoToHidden} Icon={EyeOff}>
						View hidden
					</Button>
				</div>
			)}
		</ProjectOrIssueListWrapper>
	)
}
